import React, { useEffect, useState } from 'react';
import { array, bool, object } from 'prop-types';

import { connect } from 'react-redux';

import { has, isEmpty, isNil } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import template from 'url-template';

import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import Switch from '../../../components/FormFields/Switch';
import AutoComplete from '../../../components/FormFields/Autocomplete';

import CancelButton from '../../../components/CancelButton';
import ConfirmButton from '../../../components/ConfirmButton';

import ConfirmationModal from '../../Modals/ConfirmationModal';

import useApi from '../../../hooks/useApi';

import * as ServiceManagementActions from '../../../actions/serviceManagementActions';
import * as UiActions from '../../../actions/uiActions';

import cognitoData from '../../../awsConfig';

import classes from './EmailSettings.module.css';

const SitesUrl = template.parse('/clients/{clientId}/sites{?limit}');

const DEFAULT_LIMIT = 100;

const ValidationSchema = Yup.object().shape({
    alias: Yup.string()
        .required()
});

const EmailSettings = ({
    alias,
    clientsList,
    companyInfo,
    isLoading,
    issueTypes,
    setEnableEmailToServiceCall,
    toggleLoading,
    updateEmailEnabled,
    ...props
}) => {

    const [sites, fetchSites] = useApi(clientId => ({
        url: SitesUrl.expand({
            clientId,
            limit: DEFAULT_LIMIT
        })
    }));

    const [ client, setClient ] = useState();
    const [ site, setSite ] = useState();
    const [ issueTypeString, setIssueTypeString ] = useState('');
    const [ templateExtension, setTemplateExtension ] = useState('eml');
    const [ aliasConfirmationOpen, setAliasConfirmationOpen ] = useState(false);

    const [formattedSites, setFormattedSites] = useState([]);

    const servicerequestsDomain = cognitoData.key2act.applications.servicerequests.domain;

    useEffect(() => {
        if (issueTypes.length) {
            const issueTypeNameString = issueTypes.map(type => type.name).join(', ');

            setIssueTypeString(issueTypeNameString);
        }
    }, [issueTypes]);

    useEffect(() => {
        setClient();
    }, [clientsList]);

    useEffect(() => {
        if (client) {
            setFormattedSites([]);
            fetchSites(client.id);
        }
    }, [client]);

    useEffect(() => {
        if (sites.data) {
            const payload = sites.data.map(site => ( { id: site.id, name: site.name} ));

            const formatted = [
                {
                    items:     [...payload].sort(sorter),
                    name:      'Sites',
                    itemProps: { type: 'sites'}
                }
            ];

            setFormattedSites(formatted);
        }
    }, [sites]);

    const formik = useFormik({
        initialValues: {
            alias: ''
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            const { createAlias } = props;

            toggleLoading(true);

            await createAlias(values.alias);

            toggleLoading(false);
        }
    });

    const isAutoFillArrayEmpty = array => {
        if (!array || array.length < 1) {
            return true;
        }

        if (has(array[0], 'items')) {
            return isEmpty(array[0].items);
        }

        return true;
    };

    const sorter = (a, b) => {
        let nameA = a.name.toUpperCase();
        let nameB = b.name.toUpperCase();

        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        return 0;
    };

    const handleAliasConfirmation = state => {
        setAliasConfirmationOpen(state);
    };

    const handleCancel = () => {
        formik.resetForm();
    };

    const handleEnableEmail = () => {
        setEnableEmailToServiceCall(!alias.enabled);

        if (alias.email) {
            updateEmailEnabled();
        }
    };

    const handleClientChange = event => {
        const { value } = event.target;

        if (isEmpty(value)) {
            setClient();
            setSite();
            setFormattedSites([]);

            return;
        }

        setClient(value);
        setSite();
    };

    const handleSiteChange = event => {
        const { value } = event.target;

        if (isEmpty(value)) {
            setSite();

            return;
        }

        setSite(value);
    };

    const handleTemplateExtension = event => {
        const { value } = event.target;

        setTemplateExtension(value);
    };

    const handleTemplateDownload = () => {
        const { companyId, companyName } = companyInfo;
        const { id: clientId } = client;
        const { id: siteId } = site;

        const date = new Date();

        // Future developer: please do not correct the tabbing for this content. The generated email template is formatted literally
        // based on this text. The server code that interprets this template will break if it finds whitespace in front of
        // the three *** that divide each line.
        const templateString = `data:message/rfc822 eml\nTo: ${alias.email || ''}\nDate: ${date.toUTCString()}\nSubject: Add Subject\nX-Unsent: 1\nContent-Type: text/plain; charset=utf-8\n
This email template can be used to request service from ${companyName}.
- All *** must be left in place and not deleted
- The Company ID line must remain as-is, do not change
- The Client ID line must remain as-is, do not change
- The Site ID line must remain as-is, do not change
- Please provide a single Priority: Low, Medium, or High. If an unrecognized Priority is provided the Priority will be set to Low.
- Please provide any number of accepted Issue Types: Energy, Comfort, Mechanical, Maintenance, Controls, Health and Safety. All Issue Types must be comma separated.
- Please provide a Description of your issue.
- Please provide an email subject that summarizes your issue.
***
Company ID: ${companyId}
***
Client ID: ${clientId}
***
Site ID: ${siteId}
***
Priority: Low
***
Issue Types: ${issueTypeString}
***
Description: Add your description here.
***
        `;

        const element = document.createElement('a');
        const file = new Blob([templateString], {type: 'text/plain'});

        element.href = URL.createObjectURL(file);
        element.setAttribute('download', `template.${templateExtension}`);

        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const renderAliasForm = () => {
        return (
            <React.Fragment>
                <div className={classes.settingsSectionTitle}>
                    <span>Enable Email to Service Request</span>
                    <Switch
                        checked={alias.enabled}
                        disableRipple
                        name="enabled"
                        onChange={handleEnableEmail}
                    />
                </div>
                <div className={classes.settingsSectionForm}>
                    <div className={classes.aliasWrapper}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="alias"
                                name="alias"
                                type="text"
                                disabled={!alias.enabled}
                                value={formik.values.alias}
                                label="Alias"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.alias) && Boolean(formik.touched.alias)}
                                helperText={Boolean(formik.touched.alias) && formik.errors.alias}
                            />
                        </FormControl>
                        <div className={classes.domain}>
                            @{servicerequestsDomain}
                        </div>
                    </div>
                </div>
                <div className={classes.settingsSectionButtons}>
                    <CancelButton
                        disabled={!alias.enabled}
                        className={!alias.enabled ? classes.disabled : ''}
                        onClick={handleCancel}
                    />
                    <ConfirmButton
                        label="Save"
                        isLoading={isLoading}
                        onClick={() => handleAliasConfirmation(true)}
                        disabled={!alias.enabled}
                    />
                </div>
            </React.Fragment>
        );
    };

    const renderAliasAddress = () => {
        return (
            <React.Fragment>
                <div className={classes.settingsSectionTitle}>
                    <span>Enable Email to Service Request</span>
                    <Switch
                        checked={alias.enabled}
                        disableRipple
                        name="enabled"
                        onChange={handleEnableEmail}
                    />
                </div>
                <div className={classes.settingsSectionAliasEmail}>
                    <p>Your Permanent Email Address</p>
                    <span>
                        {alias.email}
                        {alias.enabled ? '' : ' (disabled)'}
                    </span>
                </div>
            </React.Fragment>
        );
    };

    return (
        <React.Fragment>
            <div className={classes.EmailSettings}>
                <div className={classes.copyColumn}>
                    <div>
                        <div className={classes.labelHeader}>
                            <img src="/assets/images/emailOptionsIconColor.svg" alt="settings icon" />
                            <span>Email Settings</span>
                        </div>
                        <div className={classes.labelContent}>
                            <div>
                                By enabling Email to Service Request your Clients will be able to request service using a unique email address assigned to your company. Please create an email alias for your Company to make it easy for your Clients to request service via email. Email Aliases must be unique. Once an email alias is chosen it cannot be changed.
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className={classes.labelHeader}>
                            <img src="/assets/images/emailTemplatesIconColor.svg" alt="settings icon" />
                            <span>Email Templates</span>
                        </div>
                        <div className={classes.labelContent}>
                            <div>
                                Select a Client and Site and then click the Generate Email Template button to download the template your Client will need to use to request service via email.
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.formColumn}>
                    <div className={classes.formRow}>
                        <div className={classes.settingsSection}>
                            {isEmpty(alias.name) ? renderAliasForm() : renderAliasAddress()}
                        </div>
                    </div>
                    <div className={classes.formRow}>
                        <div className={classes.templateSection}>
                            <div>
                                <AutoComplete
                                    grouped
                                    label="Clients"
                                    name="client"
                                    onChange={handleClientChange}
                                    options={clientsList}
                                    placeholder="Clients"
                                    value={client}
                                />
                            </div>
                            <div>
                                <AutoComplete
                                    grouped
                                    label="Sites"
                                    name="site"
                                    onChange={handleSiteChange}
                                    options={formattedSites}
                                    placeholder="Sites"
                                    value={site}
                                    disabled={isAutoFillArrayEmpty(formattedSites)}
                                />
                            </div>
                            <div className={classes.templateSectionButton}>
                                <Select
                                    name="templateExtension"
                                    onChange={handleTemplateExtension}
                                    value={templateExtension}
                                    disabled={isNil(client)}
                                >
                                    <MenuItem value="eml">Windows Template</MenuItem>
                                    <MenuItem value="emltpl">Mac Template</MenuItem>
                                </Select>
                                <ConfirmButton
                                    label="Generate Email Template"
                                    onClick={handleTemplateDownload}
                                    disabled={isNil(client) || isNil(site)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmationModal
                open={aliasConfirmationOpen}
                close={() => handleAliasConfirmation(false)}
                confirm={formik.handleSubmit}
                title="Alias Confirmation"
                message="Once saved an Alias cannot be changed. Are you sure you wish to continue?"
            />
        </React.Fragment>
    );
};

EmailSettings.defaultProps = {
    alias:       {},
    clientsList: [],
    companyInfo: {},
    isLoading:   false,
    issueTypes:  []
};

EmailSettings.propTypes = {
    alias:       object,
    clientsList: array,
    companyInfo: object,
    isLoading:   bool,
    issueTypes:  array
};

const mapStateToProps = state => {
    return {
        alias:       state.svcManagement.alias,
        clientsList: state.svcManagement.clientsList,
        companyInfo: state.companyInfo,
        isLoading:   state.ui.isLoading,
        issueTypes:  state.svcManagement.issueTypes
    };
};

const mapDispatchToProps = {
    ...ServiceManagementActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailSettings);
